import util from '../util';

export default class Widget {
	constructor({ container }) {
		this.container = container;

		if(!this.container.id) this.container.id = `widget-${util.generateRandomString(16)}`;

		this.settings = {};
		this._urlParams = Object.fromEntries(
			[...new URLSearchParams(window.location.search)].filter(([k]) => k !== 'hash')
		);
		this.eventTypes = {};
		this.giftTypes = {};
		this.giftThresholds = [];
		this.textReplacements = [];
		this.serviceIcons = {};
		this.userData = null;
	}

	// ── Lifecycle ────────────────────────────────────────────────────────────

	async init(initialData = {}) {
		this._build();
		this._applyInitialData(initialData);
	}

	_applyInitialData(data) {
		if(data.settings) this.applySettings(data.settings);
		if(data.eventTypes) this.applyEventTypes(data.eventTypes);
		if(data.giftTypes) this.applyGiftTypes(data.giftTypes);
		if(data.giftThresholds) this.applyGiftThresholds(data.giftThresholds);
		if(data.textReplacements) this.applyTextReplacements(data.textReplacements);
		if(data.serviceIcons) this.applyServiceIcons(data.serviceIcons);
		if(data.userData) this.applyUserData(data.userData);
	}

	destroy() {}

	// ── Apply methods ────────────────────────────────────────────────────────

	applySettings(settings) {
		const base = settings || {};
		if(Object.keys(this._urlParams).length) {
			const overrides = {};
			for(const [key, raw] of Object.entries(this._urlParams)) {
				if(!(key in base)) continue;
				const existing = base[key];
				if(typeof existing === 'boolean') overrides[key] = raw === 'true' || raw === '1';
				else if(typeof existing === 'number') overrides[key] = Number(raw);
				else if(existing === null || existing === undefined) {
					try { overrides[key] = JSON.parse(raw); } catch(e) { overrides[key] = raw; }
				} else overrides[key] = raw;
			}
			this.settings = { ...base, ...overrides };
		} else {
			this.settings = base;
		}
		this._generateCSS();
	}

	applyEventTypes(data) {
		this.eventTypes = {};
		for(const et of data) this.eventTypes[et.event_type] = et;
	}

	applyEventType(eventType, data) {
		this.eventTypes[eventType] = data;
	}

	applyGiftTypes(results) {
		this.giftTypes = {};
		for(const gt of results) {
			if(!(gt.service_id in this.giftTypes)) this.giftTypes[gt.service_id] = {};
			this.giftTypes[gt.service_id][gt.gift_type] = gt;
		}
	}

	applyGiftType(serviceId, giftType, data) {
		if(!(serviceId in this.giftTypes)) this.giftTypes[serviceId] = {};
		this.giftTypes[serviceId][giftType] = data;
	}

	applyGiftThresholds(data) {
		this.giftThresholds = data;
	}

	applyTextReplacements(data) {
		const replacements = [];
		replacements.push({ expression: /^!.*/g, replacement: '', internal: true });
		if(data.replacements) {
			for(const r of data.replacements) {
				if(r.status !== 'enabled') continue;
				const flags = `g${r['case-sensitive'] ? '' : 'i'}`;
				const expression = r.regexp
					? new RegExp(r.replace, flags)
					: new RegExp(`${r.unbounded ? '' : '\\b'}${util.escapeRegExp(r.replace)}${r.unbounded ? '' : '\\b'}`, flags);
				replacements.push({ expression, replacement: r.with });
			}
		}
		replacements.push({ expression: /(((https?:\/\/)|(www\.))(\S+))/gi, replacement: '', internal: true });
		replacements.push({ expression: /((.+?)\2\2)\2+/gu, replacement: '$1', internal: true });
		replacements.push({ expression: /\s+/g, replacement: ' ', internal: true });
		this.textReplacements = replacements;
	}

	applyServiceIcons(data) {
		this.serviceIcons = Object.fromEntries(
			data.filter(s => s.icon?.url).map(s => [s.id, s.icon.url])
		);
	}

	applyUserData(data) {
		this.userData = data;
	}

	// ── CSS helpers ──────────────────────────────────────────────────────────

	_build() {}

	_generateCSS() {}

	_injectCSS(id, css) {
		if(document.getElementById(id)) return;
		const style = document.createElement('style');
		style.id = id;
		style.textContent = css;
		document.head.appendChild(style);
	}

	_setCSS(id, css) {
		let el = document.getElementById(id);
		if(!el) {
			el = document.createElement('style');
			el.id = id;
			document.head.appendChild(el);
		}
		el.textContent = css;
	}

	_updateFontLink() {
		document.getElementById('googleFontLink')?.remove();
		if(!this.settings.customFont && this.settings.fontFamily) {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.id = 'googleFontLink';
			link.href = `https://fonts.googleapis.com/css?family=${encodeURIComponent(this.settings.fontFamily)}`;
			document.head.appendChild(link);
		}
	}
}
